import api from "./httpService";

type CreateSessionPayload = {
    coachId: string;
    memberId: string;
    date: string;
    time: string;
    notes?: string;
    billingType: "package" | "pay_per_use";
    clientPrice?: number;
    paymentStatus?: "paid" | "unpaid";
};

export type SessionsQueryParams = {
    page?: number;
    limit?: number;
    coachId?: string;
    date?: string;
    startDate?: string;
    endDate?: string;
};

export type SessionsResponse = {
    rows: unknown[];
    total: number;
};

const GET_ENDPOINTS = ["/sessions", "/pt-sessions", "/ptSession", "/pt-session"];
const CREATE_ENDPOINTS = ["/sessions", "/pt-sessions", "/ptSession", "/pt-session"];
const DELETE_ENDPOINTS = ["/sessions", "/pt-sessions", "/ptSession", "/pt-session"];

const toArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
        const data = value as { rows?: unknown; data?: unknown; items?: unknown; sessions?: unknown };
        if (Array.isArray(data.rows)) return data.rows;
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.sessions)) return data.sessions;
    }
    return [];
};

const readTotal = (value: unknown, fallback: number): number => {
    if (!value || typeof value !== "object") return fallback;
    const data = value as { total?: unknown; count?: unknown; totalCount?: unknown };
    const total = data.total ?? data.count ?? data.totalCount;
    if (typeof total === "number" && Number.isFinite(total)) return total;
    if (typeof total === "string") {
        const parsed = Number(total);
        if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
};

export const getSessionsService = async (
    params?: SessionsQueryParams
): Promise<SessionsResponse> => {
    let lastError: unknown;
    const query = new URLSearchParams();

    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.coachId) query.set("coachId", params.coachId);
    if (params?.date) query.set("date", params.date);
    if (params?.startDate) query.set("startDate", params.startDate);
    if (params?.endDate) query.set("endDate", params.endDate);

    for (const endpoint of GET_ENDPOINTS) {
        try {
            const queryString = query.toString();
            const response = await api.get(`${endpoint}${queryString ? `?${queryString}` : ""}`);
            const rows = toArray(response.data);
            return {
                rows,
                total: readTotal(response.data, rows.length),
            };
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError;
};

export const createSessionService = async (payload: CreateSessionPayload) => {
    let lastError: unknown;

    for (const endpoint of CREATE_ENDPOINTS) {
        try {
            const response = await api.post(endpoint, payload);
            return response.data;
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError;
};

export const deleteSessionService = async (sessionId: string) => {
    let lastError: unknown;

    for (const endpoint of DELETE_ENDPOINTS) {
        try {
            const response = await api.delete(`${endpoint}/${sessionId}`);
            return response.data;
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError;
};

export const updateSessionPaymentService = async (
    sessionId: string,
    paymentStatus: "paid" | "unpaid"
) => {
    const response = await api.patch(`/sessions/${sessionId}/payment`, { paymentStatus });
    return response.data;
};
